import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import {
  apiGetBrands,
  apiCreateBrand,
  apiUpdateBrand,
  apiDeleteBrand,
  formatApiError,
} from '../../lib/api';
import { toast } from 'sonner';

const emptyForm = {
  name: '',
  category: '',
  slug: '',
  custom_logo: '',
  text_logo_text: '',
  text_logo_color: '#000000',
  text_logo_subtitle: '',
  display_order: 0,
};

export default function BrandsAdmin() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const loadBrands = async () => {
    setLoading(true);
    try {
      const { data } = await apiGetBrands();
      setBrands(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Failed to load brands');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBrands();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, display_order: brands.length + 1 });
    setDialogOpen(true);
  };

  const openEdit = (brand) => {
    setEditingId(brand.id);
    setForm({
      name: brand.name,
      category: brand.category,
      slug: brand.slug || '',
      custom_logo: brand.custom_logo || '',
      text_logo_text: brand.text_logo_text || '',
      text_logo_color: brand.text_logo_color || '#000000',
      text_logo_subtitle: brand.text_logo_subtitle || '',
      display_order: brand.display_order || 0,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Send only relevant fields
      const payload = {
        name: form.name,
        category: form.category,
        slug: form.slug || null,
        custom_logo: form.custom_logo || null,
        text_logo_text: form.text_logo_text || null,
        text_logo_color: form.text_logo_color || null,
        text_logo_subtitle: form.text_logo_subtitle || null,
        display_order: parseInt(form.display_order) || 0,
      };
      if (editingId) {
        await apiUpdateBrand(editingId, payload);
        toast.success('Brand updated successfully');
      } else {
        await apiCreateBrand(payload);
        toast.success('Brand added successfully');
      }
      setDialogOpen(false);
      loadBrands();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Failed to save brand');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this brand?')) return;
    try {
      await apiDeleteBrand(id);
      toast.success('Brand deleted');
      loadBrands();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="brands-admin">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Brand Portfolio</h2>
          <p className="text-gray-500">Manage brands you've worked with</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openCreate}
              className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white font-bold"
              data-testid="add-brand-button"
            >
              <Plus size={18} className="mr-2" />
              Add Brand
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Brand' : 'Add New Brand'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Brand Name *</Label>
                  <Input
                    id="name"
                    data-testid="brand-form-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Input
                    id="category"
                    data-testid="brand-form-category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder="e.g. Banking, Tech"
                    required
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
                <p className="font-bold mb-1">Logo Options (pick ONE):</p>
                <ol className="list-decimal ml-5 space-y-1 text-xs">
                  <li><b>Simple Icons Slug</b> - Use brand slug from simpleicons.org (e.g., google, samsung)</li>
                  <li><b>Custom Logo</b> - Predefined: "Microsoft", "Adobe", or "OpenAI"</li>
                  <li><b>Text Logo</b> - Custom styled text with color (fallback for any brand)</li>
                </ol>
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">Simple Icons Slug (option 1)</Label>
                <Input
                  id="slug"
                  data-testid="brand-form-slug"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="e.g. google, samsung, airtel"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="custom_logo">Custom Logo (option 2)</Label>
                <select
                  id="custom_logo"
                  data-testid="brand-form-custom-logo"
                  value={form.custom_logo}
                  onChange={(e) => setForm({ ...form, custom_logo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">None</option>
                  <option value="Microsoft">Microsoft</option>
                  <option value="Adobe">Adobe</option>
                  <option value="OpenAI">OpenAI</option>
                </select>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-bold mb-3">Text Logo (option 3)</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="text_logo_text">Text</Label>
                    <Input
                      id="text_logo_text"
                      data-testid="brand-form-text-logo-text"
                      value={form.text_logo_text}
                      onChange={(e) => setForm({ ...form, text_logo_text: e.target.value })}
                      placeholder="e.g. ACKO"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="text_logo_color">Color</Label>
                    <Input
                      id="text_logo_color"
                      data-testid="brand-form-text-logo-color"
                      type="color"
                      value={form.text_logo_color}
                      onChange={(e) => setForm({ ...form, text_logo_color: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2 mt-3">
                  <Label htmlFor="text_logo_subtitle">Subtitle (optional)</Label>
                  <Input
                    id="text_logo_subtitle"
                    data-testid="brand-form-text-logo-subtitle"
                    value={form.text_logo_subtitle}
                    onChange={(e) => setForm({ ...form, text_logo_subtitle: e.target.value })}
                    placeholder="e.g. Bank, School"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="display_order">Display Order</Label>
                <Input
                  id="display_order"
                  data-testid="brand-form-display-order"
                  type="number"
                  value={form.display_order}
                  onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="brand-form-cancel">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white"
                  data-testid="brand-form-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : editingId ? (
                    'Update Brand'
                  ) : (
                    'Add Brand'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-blue-600" size={32} />
        </div>
      ) : brands.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">No brands yet. Click "Add Brand" to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {brands.map((brand) => (
            <Card key={brand.id} className="border-2 border-gray-100 hover:border-blue-300 transition-all" data-testid={`brand-card-${brand.id}`}>
              <CardHeader className="space-y-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{brand.name}</CardTitle>
                    <CardDescription>{brand.category}</CardDescription>
                  </div>
                  <span className="text-xs font-bold text-gray-500">#{brand.display_order}</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="text-xs text-gray-500 space-y-1">
                  {brand.slug && <p>Slug: <code className="bg-gray-100 px-1 rounded">{brand.slug}</code></p>}
                  {brand.custom_logo && <p>Custom: <code className="bg-gray-100 px-1 rounded">{brand.custom_logo}</code></p>}
                  {brand.text_logo_text && (
                    <p>
                      Text: <span style={{ color: brand.text_logo_color }} className="font-bold">{brand.text_logo_text}</span>
                      {brand.text_logo_subtitle && ` ${brand.text_logo_subtitle}`}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(brand)}
                    className="flex-1"
                    data-testid={`edit-brand-${brand.id}`}
                  >
                    <Pencil size={14} className="mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(brand.id)}
                    className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                    data-testid={`delete-brand-${brand.id}`}
                  >
                    <Trash2 size={14} className="mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
